import type { AccessPolicy, AccessPolicyUpdate } from "./_shared"

import { api } from "@/lib/api-client"

// Tools Management
export interface ToolStatus {
  name: string
  description: string
  tags: string[]
  version: string
  enabled: boolean
  access_configured?: boolean
  access_overridden?: boolean
}

export async function getAgentTools(agentName: string): Promise<ToolStatus[]> {
  const response = await api.get(`/admin/agents/${agentName}/tools`)
  return response.data || response
}

export async function updateToolStatus(
  agentName: string,
  toolName: string,
  enabled: boolean
): Promise<{
  success: boolean
  agent_name: string
  tool_name: string
  enabled: boolean
  message: string
}> {
  const response = await api.put(`/admin/agents/${agentName}/tools/${toolName}/status`, { enabled })
  const data = response.data || response

  if (data.success === false) {
    throw new Error(data.message || `Failed to update tool ${toolName} status`)
  }

  return data
}

export async function updateToolsStatusBatch(
  agentName: string,
  updates: Record<string, boolean>
): Promise<{
  success: boolean
  agent_name: string
  updated_tools: string[]
  message: string
}> {
  const response = await api.put(`/admin/agents/${agentName}/tools/status/batch`, { updates })
  const data = response.data || response

  if (data.success === false) {
    throw new Error(data.message || "Failed to batch update tool statuses")
  }

  return data
}

// Tool Actions Management
export interface ToolAction {
  name: string
  description: string
  enabled: boolean
  access_configured?: boolean
  access_overridden?: boolean
}

export async function getToolActions(agentName: string, toolName: string): Promise<ToolAction[]> {
  const response = await api.get(`/admin/agents/${agentName}/tools/${toolName}/actions`)
  return response.data || response
}

export async function updateToolActionStatus(params: {
  agentName: string
  toolName: string
  actionName: string
  enabled: boolean
}): Promise<{
  success: boolean
  agent_name: string
  tool_name: string
  action_name: string
  enabled: boolean
  message: string
}> {
  const { agentName, toolName, actionName, enabled } = params
  const response = await api.put(
    `/admin/agents/${agentName}/tools/${toolName}/actions/${actionName}/status`,
    { enabled }
  )
  const data = response.data || response

  if (data.success === false) {
    throw new Error(data.message || `Failed to update action ${actionName} status`)
  }

  return data
}

export async function updateToolActionsStatusBatch(
  agentName: string,
  toolName: string,
  updates: Record<string, boolean>
): Promise<{
  success: boolean
  agent_name: string
  tool_name: string
  updated_actions: string[]
  message: string
}> {
  const response = await api.put(
    `/admin/agents/${agentName}/tools/${toolName}/actions/status/batch`,
    { updates }
  )
  const data = response.data || response

  if (data.success === false) {
    throw new Error(data.message || "Failed to batch update action statuses")
  }

  return data
}

export async function getToolAccessPolicy(
  agentName: string,
  toolName: string
): Promise<AccessPolicy> {
  const response = await api.get(`/admin/agents/${agentName}/tools/${toolName}/access`)
  const data = response.data || response
  return data.access || {}
}

export async function getToolsBaseAccessPolicy(agentName: string): Promise<AccessPolicy> {
  const response = await api.get(`/admin/agents/${agentName}/tools/base/access`)
  const data = response.data || response
  return data.access || {}
}

export async function updateToolAccessPolicy(
  agentName: string,
  toolName: string,
  policy: AccessPolicyUpdate
): Promise<AccessPolicy> {
  const response = await api.put(`/admin/agents/${agentName}/tools/${toolName}/access`, policy)
  const data = response.data || response
  return data.access || {}
}

export async function updateToolsBaseAccessPolicy(
  agentName: string,
  policy: AccessPolicyUpdate
): Promise<AccessPolicy> {
  const response = await api.put(`/admin/agents/${agentName}/tools/base/access`, policy)
  const data = response.data || response
  return data.access || {}
}

export async function clearToolAccessPolicy(
  agentName: string,
  toolName: string
): Promise<AccessPolicy> {
  const response = await api.put(`/admin/agents/${agentName}/tools/${toolName}/access`, {})
  const data = response.data || response
  return data.access || {}
}

export type ToolConfig = Record<string, unknown>

export async function getToolConfig(agentName: string, toolName: string): Promise<ToolConfig> {
  const response = await api.get(`/admin/agents/${agentName}/tools/${toolName}/config`)
  const data = response.data || response
  const config = data?.config
  return config && typeof config === "object" ? (config as ToolConfig) : {}
}

export async function updateToolConfig(
  agentName: string,
  toolName: string,
  config: ToolConfig
): Promise<ToolConfig> {
  const response = await api.patch(`/admin/agents/${agentName}/tools/${toolName}/config`, {
    config,
  })
  const data = response.data || response
  const next = data?.config
  return next && typeof next === "object" ? (next as ToolConfig) : {}
}

export async function getToolActionAccessPolicy(
  agentName: string,
  toolName: string,
  actionName: string
): Promise<AccessPolicy> {
  const response = await api.get(
    `/admin/agents/${agentName}/tools/${toolName}/actions/${actionName}/access`
  )
  const data = response.data || response
  return data.access || {}
}

export async function updateToolActionAccessPolicy({
  agentName,
  toolName,
  actionName,
  policy,
}: {
  agentName: string
  toolName: string
  actionName: string
  policy: AccessPolicyUpdate
}): Promise<AccessPolicy> {
  const response = await api.put(
    `/admin/agents/${agentName}/tools/${toolName}/actions/${actionName}/access`,
    policy
  )
  const data = response.data || response
  return data.access || {}
}
